import {
  animate,
  motionValue,
  type AnimationPlaybackControls,
  type MotionValue,
} from 'motion/react';

import {
  LOGO_EASE,
  WING_KEYFRAMES,
  WING_TIMES,
} from '@/assets/icons/logo-motion';

import { type AnimationScope } from './animation-scope';

const DART_EASE = [0.165, 0.84, 0.44, 1] as const;
const FLUTTER_DURATION = 0.9;
const PREEN_TIMES = [0, 0.15, 0.3, 0.38, 0.46, 0.54, 0.7, 1];

export type LogoAct =
  | 'stillness'
  | 'ponder'
  | 'preen'
  | 'hopTurn'
  | 'crouch'
  | 'sip'
  | 'stretch'
  | 'flutter'
  | 'dart'
  | 'glide';

export type LogoMotionValues = {
  wingAngle: MotionValue<number>;
  wingFold: MotionValue<number>;
  x: MotionValue<number>;
  y: MotionValue<number>;
  rotate: MotionValue<number>;
  scaleX: MotionValue<number>;
  scaleY: MotionValue<number>;
  opacity: MotionValue<number>;
  eyeGaze: MotionValue<number>;
  perch: MotionValue<number>;
};

export const createLogoMotionValues = (): LogoMotionValues => ({
  wingAngle: motionValue(0),
  wingFold: motionValue(0),
  x: motionValue(0),
  y: motionValue(0),
  rotate: motionValue(0),
  scaleX: motionValue(1),
  scaleY: motionValue(1),
  opacity: motionValue(1),
  eyeGaze: motionValue(0),
  perch: motionValue(0),
});

export const resetPose = (values: LogoMotionValues, wingFold: 0 | 1) => {
  values.x.jump(0);
  values.y.jump(0);
  values.rotate.jump(0);
  values.scaleX.jump(1);
  values.scaleY.jump(1);
  values.opacity.jump(1);
  values.eyeGaze.jump(0);
  values.wingAngle.jump(0);
  values.wingFold.jump(wingFold);
  values.perch.jump(wingFold);
};

type LogoActDeps = Pick<AnimationScope, 'track' | 'rest'>;

export const createLogoActs = (
  values: LogoMotionValues,
  { track, rest }: LogoActDeps
) => {
  const {
    wingAngle,
    wingFold,
    x,
    y,
    rotate,
    scaleX,
    scaleY,
    opacity,
    eyeGaze,
    perch,
  } = values;

  const flap = (duration = FLUTTER_DURATION) =>
    track(
      animate(wingAngle, WING_KEYFRAMES, {
        duration,
        times: WING_TIMES,
        ease: LOGO_EASE,
      })
    );

  const hop = async (faceDirection: 1 | -1) => {
    track(animate(scaleY, [1, 0.93, 1], { duration: 0.22, ease: 'easeOut' }));
    await track(animate(y, 0.5, { duration: 0.11, ease: 'easeOut' }));
    await track(animate(y, -2.2, { duration: 0.16, ease: DART_EASE }));
    scaleX.jump(faceDirection);
    await track(animate(y, 0, { duration: 0.16, ease: 'easeIn' }));
    await track(
      animate(scaleY, [0.92, 1.03, 1], { duration: 0.22, ease: 'easeOut' })
    );
  };

  const land = async () => {
    track(animate(wingFold, 1, { duration: 0.14, ease: 'easeIn', delay: 0.1 }));
    track(animate(perch, 1, { duration: 0.2, ease: 'easeOut', delay: 0.12 }));
    await track(animate(y, 0.6, { duration: 0.22, ease: 'easeOut' }));
    await track(animate(y, 0, { duration: 0.26, ease: 'easeOut' }));
  };

  const takeoff = async () => {
    track(animate(scaleY, [1, 0.93, 1], { duration: 0.24, ease: 'easeInOut' }));
    await track(animate(y, 0.7, { duration: 0.12, ease: 'easeOut' }));
    track(animate(wingFold, 0, { duration: 0.1, ease: 'easeOut' }));
    track(animate(perch, 0, { duration: 0.18, ease: 'easeOut', delay: 0.05 }));
    flap(0.7);
    await track(animate(y, -1.6, { duration: 0.22, ease: DART_EASE }));
    await track(animate(y, 0, { duration: 0.3, ease: 'easeOut' }));
  };

  const exitFlight = async () => {
    scaleX.jump(1);
    track(animate(eyeGaze, 0, { duration: 0.12, ease: 'easeOut' }));
    track(animate(rotate, -3, { duration: 0.14, ease: 'easeOut' }));
    await track(
      animate(scaleY, [scaleY.get(), 0.93, 1.05, 1], {
        duration: 0.26,
        times: [0, 0.35, 0.75, 1],
        ease: 'easeOut',
      })
    );
    track(animate(rotate, 0, { duration: 0.2, ease: 'easeOut' }));
    track(animate(wingFold, 0, { duration: 0.08, ease: 'easeOut' }));
    track(animate(perch, 0, { duration: 0.16, ease: 'easeOut' }));
    track(
      animate(wingAngle, [wingAngle.get(), -50, -12, -42, 0], {
        duration: 0.32,
        ease: 'easeOut',
      })
    );
    track(animate(y, -3.5, { duration: 0.3, ease: DART_EASE }));
    track(
      animate(opacity, 0, { duration: 0.22, delay: 0.08, ease: 'easeOut' })
    );
    await track(animate(x, 12, { duration: 0.3, ease: DART_EASE }));
  };

  const acts: Record<LogoAct, () => Promise<void>> = {
    stillness: async () => {
      await track(
        animate(scaleY, [1, 1.03, 1, 1.024, 1], {
          duration: 1.6,
          times: [0, 0.3, 0.55, 0.8, 1],
          ease: 'easeInOut',
        })
      );
    },
    ponder: async () => {
      track(
        animate(eyeGaze, [0, 1, 1, 0], {
          duration: 2.4,
          times: [0, 0.16, 0.82, 1],
          ease: 'easeInOut',
        })
      );
      track(
        animate(y, [0, -0.3, -0.3, 0], {
          duration: 2.4,
          times: [0, 0.15, 0.85, 1],
          ease: 'easeInOut',
        })
      );
      await track(
        animate(rotate, [rotate.get(), -9, -8, -9.5, -8.6, 0], {
          duration: 2.4,
          times: [0, 0.14, 0.4, 0.62, 0.88, 1],
          ease: 'easeInOut',
        })
      );
    },
    preen: async () => {
      track(
        animate(x, [0, -0.7, -0.7, -0.7, -0.7, -0.7, -0.7, 0], {
          duration: 1.5,
          times: PREEN_TIMES,
          ease: 'easeInOut',
        })
      );
      track(
        animate(y, [0, 0.7, 0.7, 0.9, 0.7, 0.9, 0.7, 0], {
          duration: 1.5,
          times: PREEN_TIMES,
          ease: 'easeInOut',
        })
      );
      await track(
        animate(rotate, [rotate.get(), 21, 21, 24, 21, 24, 21, 0], {
          duration: 1.5,
          times: PREEN_TIMES,
          ease: 'easeInOut',
        })
      );
    },
    crouch: async () => {
      track(
        animate(y, [0, 1.3, -0.3, 0], {
          duration: 0.55,
          times: [0, 0.4, 0.75, 1],
          ease: 'easeInOut',
        })
      );
      await track(
        animate(scaleY, [1, 0.88, 1.04, 1], {
          duration: 0.55,
          times: [0, 0.4, 0.75, 1],
          ease: 'easeInOut',
        })
      );
    },
    sip: async () => {
      track(
        animate(x, [0, 1.3, 1.3, 1.3, 0], {
          duration: 1.4,
          times: [0, 0.2, 0.5, 0.8, 1],
          ease: 'easeInOut',
        })
      );
      await track(
        animate(rotate, [rotate.get(), 9, 12, 9, 12, 9, 0], {
          duration: 1.4,
          times: [0, 0.2, 0.35, 0.5, 0.65, 0.8, 1],
          ease: 'easeInOut',
        })
      );
    },
    stretch: async () => {
      track(
        animate(rotate, [rotate.get(), -3, -3, 0], {
          duration: 1.2,
          times: [0, 0.25, 0.75, 1],
          ease: 'easeInOut',
        })
      );
      await track(animate(wingFold, 0, { duration: 0.15, ease: 'easeOut' }));
      await track(animate(wingAngle, -26, { duration: 0.2, ease: 'easeOut' }));
      await rest(500);
      await track(animate(wingAngle, 0, { duration: 0.25, ease: 'easeInOut' }));
      await track(animate(wingFold, 1, { duration: 0.15, ease: 'easeIn' }));
    },
    hopTurn: async () => {
      await hop(-1);
      await track(
        animate(rotate, [rotate.get(), -5, -5, 0], {
          duration: 0.7,
          times: [0, 0.15, 0.8, 1],
          ease: 'easeInOut',
        })
      );
      await hop(1);
    },
    flutter: async () => {
      flap();
      await track(
        animate(y, [0, -1.2, -0.2, -0.8, 0], {
          duration: FLUTTER_DURATION,
          times: [0, 0.2, 0.5, 0.75, 1],
          ease: LOGO_EASE,
        })
      );
    },
    dart: async () => {
      flap(0.5);
      track(animate(scaleX, [1, 1.16, 1], { duration: 0.35, ease: DART_EASE }));
      await track(animate(x, 3.5, { duration: 0.35, ease: DART_EASE }));
      await track(
        animate(y, [0, -0.8, 0], { duration: 0.45, ease: 'easeInOut' })
      );
      flap(0.5);
      track(animate(scaleX, [1, 1.16, 1], { duration: 0.35, ease: DART_EASE }));
      await track(animate(x, 0, { duration: 0.35, ease: DART_EASE }));
    },
    glide: async () => {
      await track(animate(wingAngle, -16, { duration: 0.2, ease: 'easeOut' }));
      await track(
        animate(y, [0, -1.6, -1.6, 0], {
          duration: 1.6,
          times: [0, 0.35, 0.6, 1],
          ease: 'easeInOut',
        })
      );
      await track(animate(wingAngle, 0, { duration: 0.25, ease: 'easeOut' }));
    },
  };

  return { acts, land, takeoff, exitFlight };
};

export const playStartle = (
  values: LogoMotionValues
): AnimationPlaybackControls[] => {
  const { y, rotate, scaleY, wingFold, wingAngle } = values;
  return [
    animate(scaleY, [scaleY.get(), 0.8, 1.1, 0.9, 1.03, 1], {
      duration: 0.6,
      times: [0, 0.12, 0.35, 0.6, 0.8, 1],
      ease: 'easeOut',
    }),
    animate(y, [y.get(), 1, -4.5, -4.5, 0], {
      duration: 0.6,
      times: [0, 0.12, 0.42, 0.52, 1],
      ease: ['easeOut', DART_EASE, 'linear', 'easeIn'],
    }),
    animate(wingFold, [wingFold.get(), 0, 0, 1], {
      duration: 0.75,
      times: [0, 0.15, 0.75, 1],
      ease: 'easeOut',
    }),
    animate(wingAngle, [0, -60, -10, -55, 0], {
      duration: 0.42,
      delay: 0.08,
      ease: 'easeInOut',
    }),
    animate(rotate, [rotate.get(), -10, -10, 0, 6, 6, -4, -4, 0], {
      duration: 1.3,
      times: [0, 0.15, 0.3, 0.45, 0.55, 0.75, 0.8, 0.95, 1],
      ease: 'easeInOut',
    }),
  ];
};

export const playNotice = (
  values: LogoMotionValues,
  tilt: number
): AnimationPlaybackControls[] => [
  animate(values.rotate, [values.rotate.get(), tilt, tilt, 0], {
    duration: 1.1,
    times: [0, 0.18, 0.72, 1],
    ease: 'easeInOut',
  }),
];
